import React, { useEffect, useState } from "react";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { flattenErrors } from './ErrorHelpers';
import axios from 'axios';
import moment from 'moment';

const years = Array.from(new Array(20), (x, i) => i + new Date().getFullYear());
export function UpdateRecurringPaymentsComponent({ accountNumber, children }) {
    const [modalSettings, setModalSettings] = useState({
        showModal: false,
        renderModal: false
    });

    return (
        <>
            <div onClick={() => setModalSettings({
                showModal: true,
                renderModal: true
            })}>
                {children}
            </div>
            {modalSettings.renderModal &&
                <UpdateRecurringPaymentsModalComponent
                    accountNumber={accountNumber}
                    showModal={modalSettings.showModal}
                    handleClose={() => setModalSettings({ showModal: false })}
                    onExited={() => setModalSettings({ renderModal: false })} />}
        </>
    );
}

function UpdateRecurringPaymentsModalComponent({ accountNumber, showModal, handleClose, onExited }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [recurringPaymentInfo, setRecurringPaymentInfo] = useState({});
    const [updateCardInfo, setUpdateCardInfo] = useState({});
    const [errors, setErrors] = useState([]);
    const [showSuccessScreen, setShowSuccessScreen] = useState(false);

    useEffect(() => {
        axios.get(`/api/talent/${accountNumber}/recurringpayments`).then(resp => {
            setLoading(false);
            setRecurringPaymentInfo(resp.data);
        });
    }, []);

    function handleInputChange(e) {
        setUpdateCardInfo({
            ...updateCardInfo,
            [e.target.name]: e.target.value
        });
    }

    function modalWithContent(children) {
        return (
            <Modal isOpen={showModal} onClosed={onExited}>
                <ModalHeader toggle={handleClose}>
                    Update Recurring Payments
                </ModalHeader>
                <ModalBody>
                    {children}
                </ModalBody>
            </Modal>
        );
    }

    if (loading) {
        return modalWithContent(<div className="text-center">Loading...</div>);
    }

    if (recurringPaymentInfo.canUpdateRecurringPayments === false) {
        return modalWithContent(<div className="text-center">{recurringPaymentInfo.message}</div>);
    }

    if (showSuccessScreen) {
        return modalWithContent(<div className="text-center">Recurring payments updated successfully!</div>);
    }

    const amount = parseFloat(recurringPaymentInfo.amountOfPayments);
    const numberOfRemainingRecurringPayments = recurringPaymentInfo.numberOfRemainingRecurringPayments;
    const nextPaymentDateFormatted = new moment(recurringPaymentInfo.nextPaymentDate).format("MMMM DD, YYYY");
    const currentCreditCardNumber = recurringPaymentInfo.currentCreditCardNumber;

    function save() {
        setSaving(true);
        setErrors([]);
        axios.post(`/api/talent/${accountNumber}/recurringpayments/change`,
            {
                expectedNumberOfRemainingRecurringPayments: numberOfRemainingRecurringPayments,
                expectedStartDate: recurringPaymentInfo.nextPaymentDate,
                expectedAmountOfPayments: amount,
                ...updateCardInfo
            }).then(resp => {
                setShowSuccessScreen(true);
            }).catch(error => {
                if (error.response) {
                    if (error.response.status === 400) {
                        if (typeof error.response.data === "string") {
                            setErrors([error.response.data]);
                        } else {
                            setErrors(flattenErrors(error.response.data.errors));
                        }
                    }
                } else if (error.request) {
                    console.log(error.request);
                }
            })
            .then(() => setSaving(false));
    }

    return (
        <Modal isOpen={showModal} onClosed={onExited}>
            <ModalHeader toggle={handleClose}>
                Update Recurring Payments
            </ModalHeader>
            <ModalBody>
                {recurringPaymentInfo.canUpdateRecurringPayments && <div>
                    <h4>Current Recurring Payments</h4>
                    <div className="row">
                        <div className="col-sm-6 bold">
                            Current Card Number:
                        </div>
                        <div className="col-sm-6">
                            {currentCreditCardNumber}
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-sm-6 bold">
                            # Remaining:
                        </div>
                        <div className="col-sm-6">
                            {numberOfRemainingRecurringPayments}
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-sm-6 bold">
                            Amount:
                        </div>
                        <div className="col-sm-6">
                            ${amount.toFixed(2)}
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-sm-6 bold">
                            Next Payment Date:
                        </div>
                        <div className="col-sm-6">
                            {nextPaymentDateFormatted}
                        </div>
                    </div>

                    <hr />

                    <h4>Update Recurring Payment Card</h4>

                    <p>
                        You can use the form below to update your credit card information for recurring payments.
                    </p>

                    <div className="form-row">
                        <div className="form-group col-12">
                            <label className="sr-only">Card Number</label>
                            <input type="tel" name="CreditCardNumber" value={updateCardInfo.CreditCardNumber} onChange={handleInputChange}
                                placeholder="4111 0001 1234 4567" className="form-control" disabled={saving} />
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="col-6 form-group">
                            <label className="sr-only">Exp. Month</label>
                            <select name="ExpirationMonth" className="form-control" value={updateCardInfo.ExpirationMonth}
                                onChange={handleInputChange} disabled={saving}>
                                <option>Month</option>
                                <option value="1">January</option>
                                <option value="2">February</option>
                                <option value="3">March</option>
                                <option value="4">April</option>
                                <option value="5">May</option>
                                <option value="6">June</option>
                                <option value="7">July</option>
                                <option value="8">August</option>
                                <option value="9">September</option>
                                <option value="10">October</option>
                                <option value="11">November</option>
                                <option value="12">December</option>
                            </select>
                        </div>
                        <div className="col-4 form-group">
                            <label className="sr-only">Exp. Year</label>
                            <select name="ExpirationYear" className="form-control" value={updateCardInfo.ExpirationYear}
                                onChange={handleInputChange} disabled={saving}>
                                <option>Year</option>
                                {years.map(y => <option value={y}>{y}</option>)}
                            </select>
                        </div>
                        <div className="col-2 form-group">
                            <label className="sr-only">CVV Code</label>
                            <input type="tel" name="CvvCode" className="form-control" maxlength="4" placeholder="CVV" style={{ width: "60px" }}
                                value={updateCardInfo.CvvCode} onChange={handleInputChange} disabled={saving} />
                        </div>
                        <div className="form-check">
                            <input className="form-check-input" type="checkbox" id="accept-terms" checked={updateCardInfo.acceptTerms}
                                disabled={saving} name="acceptTerms" onChange={e => handleInputChange({
                                    target: {
                                        name: e.target.name,
                                        value: e.target.checked
                                    }
                                })} />
                            <label className="form-check-label" htmlFor="accept-terms">
                                I agree to change my recurring payment to the credit card above.
                                My card will be charged {numberOfRemainingRecurringPayments} times
                                in the amount of ${amount.toFixed(2)}{' '}
                                on a {recurringPaymentInfo.interval} basis 
                                starting on {nextPaymentDateFormatted}.
                            </label>
                        </div>
                    </div>
                </div>}
            </ModalBody>
            <ModalFooter>
                {saving && <div className="float-left">
                    Sending...
                    </div>}
                {errors && <div className="float-left text-left">
                    {errors.map(e => <div key={e}>{e}</div>)}
                </div>}
                <img src="/PayTraceSeal_3l.png" className="mr-auto" style={{ height: '40px' }} />
                <Button onClick={save} disabled={saving}>
                    Update Card
                </Button>
            </ModalFooter>
        </Modal>
    );
}